import React, { useState, useEffect, useRef } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

// Severity patterns matching AdminHealth.js
const parseLogSeverity = (line) => {
  const patterns = {
    error: /\[ERROR\]|\bERROR\b|❌|Failed|Exception|ENOENT|Error:/i,
    warn: /\[WARN\]|\bWARN\b|⚠️|Warning/i,
    success: /\[SUCCESS\]|✅|Completed|SUCCESS|Script completed/i,
    info: /\[INFO\]|\bINFO\b|ℹ️|Starting|Sending|Found|Fetched/i,
  };
  for (const [level, regex] of Object.entries(patterns)) {
    if (regex.test(line)) return level;
  }
  return 'default';
};

const highlightSearchTerm = (text, term) => {
  if (!term || term.length < 2) return text;
  try {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} style={{ background: '#fef08a', borderRadius: '2px' }}>{part}</mark>
        : part
    );
  } catch {
    return text;
  }
};

const SEVERITY_STYLES = {
  error: { background: '#fef2f2', color: '#dc2626' },
  warn: { background: '#fffbeb', color: '#b45309' },
  success: { background: '#f0fdf4', color: '#15803d' },
  info: {},
  default: {},
};

const FILTER_CHIPS = [
  { id: 'all', label: 'All', icon: '📋' },
  { id: 'error', label: 'Errors', icon: '❌' },
  { id: 'warn', label: 'Warnings', icon: '⚠️' },
  { id: 'success', label: 'Success', icon: '✅' },
];

// ============================================================
// ScriptLogsTab
// Usage: <ScriptLogsTab user={user} />
// ============================================================

const ScriptLogsTab = ({ user }) => {
  const [scriptLogs, setScriptLogs] = useState([]);
  const [selectedScript, setSelectedScript] = useState('');
  const [logContent, setLogContent] = useState([]); // array of line strings
  const [searchTerm, setSearchTerm] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logOffset, setLogOffset] = useState(0);
  const [logLimit] = useState(100);
  const [totalLines, setTotalLines] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState('technical'); // 'technical' or 'human'
  const [humanEvents, setHumanEvents] = useState([]);

  const searchInputRef = useRef(null);

  // ---- helpers ----

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const apiFetch = async (endpoint, options = {}) => {
    setLoading(true);
    try {
      // Identity travels as the bearer token added by adminFetch.
      const headers = { 'Content-Type': 'application/json' };
      const res = await adminFetch(
        `${API_URL}${endpoint}`,
        { ...options, headers: { ...headers, ...options.headers } }
      );
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        await res.text();
        throw new Error(`Server response was not JSON. Status: ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      return data;
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ---- data fetching ----

  const fetchScriptList = async () => {
    const data = await apiFetch('/api/admin/logs/scripts');
    if (data && data.logs) {
      setScriptLogs(data.logs);
      if (data.logs.length > 0 && !selectedScript) {
        const first = data.logs[0].script;
        setSelectedScript(first);
        loadScriptLog(first, 0, '');
      }
    }
  };

  const loadScriptLog = async (scriptName, offset = 0, search = searchTerm) => {
    if (!scriptName) return;
    setSelectedScript(scriptName);
    setLogOffset(offset);
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

    // Fetch both technical and human views in parallel
    const [techData, humanData] = await Promise.all([
      apiFetch(`/api/admin/logs/scripts/${scriptName}?offset=${offset}&limit=${logLimit}${searchParam}`),
      adminFetch(`${API_URL}/api/admin/logs/scripts/${scriptName}/human`)
        .then(r => r.json()).catch(() => ({ events: [] }))
    ]);

    if (techData) {
      setLogContent(techData.lines || []);
      setTotalLines(techData.totalLines || 0);
      setHasMore(techData.hasMore || false);
    }
    setHumanEvents(humanData?.events || []);
  };

  const refreshScriptLog = async () => {
    if (!selectedScript) return;
    setIsRefreshing(true);
    await loadScriptLog(selectedScript, logOffset, searchTerm);
    setIsRefreshing(false);
    showToast('Logs refreshed', 'info');
  };

  // window.open can't carry the bearer token, so pull the file and save the blob.
  const downloadScriptLog = async () => {
    if (!selectedScript) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/logs/scripts/${selectedScript}/download`);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedScript;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e.message || 'Download failed', 'error');
    }
  };

  // ---- pagination ----

  const loadPreviousPage = () => {
    if (logOffset >= logLimit) {
      loadScriptLog(selectedScript, logOffset - logLimit, searchTerm);
    }
  };

  const loadNextPage = () => {
    if (hasMore) {
      loadScriptLog(selectedScript, logOffset + logLimit, searchTerm);
    }
  };

  // ---- effects ----

  useEffect(() => {
    fetchScriptList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 5s, paused when document is hidden
  useEffect(() => {
    if (!autoRefresh || !selectedScript) return;
    const interval = setInterval(() => {
      if (!document.hidden) refreshScriptLog();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedScript, logOffset, searchTerm]);

  // Keyboard shortcuts: '/' to focus search, 'r' to refresh
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'r') {
        e.preventDefault();
        refreshScriptLog();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScript, logOffset, searchTerm]);

  // ---- derived data ----

  const processedLines = logContent.map((line, index) => ({
    number: logOffset + index + 1,
    content: line,
    severity: parseLogSeverity(line),
  }));

  const filteredLines = processedLines.filter((line) => {
    if (quickFilter === 'all') return true;
    return line.severity === quickFilter;
  });

  const logCounts = {
    all: processedLines.length,
    error: processedLines.filter((l) => l.severity === 'error').length,
    warn: processedLines.filter((l) => l.severity === 'warn').length,
    success: processedLines.filter((l) => l.severity === 'success').length,
  };

  const handleCopyLine = (line) => {
    navigator.clipboard.writeText(line).then(() => {
      showToast('Copied to clipboard!', 'info');
    });
  };

  const handleScriptChange = (e) => {
    const name = e.target.value;
    setSearchTerm('');
    setLogOffset(0);
    setQuickFilter('all');
    setLogContent([]);
    loadScriptLog(name, 0, '');
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setLogOffset(0);
      loadScriptLog(selectedScript, 0, searchTerm);
    }
    if (e.key === 'Escape') {
      setSearchTerm('');
      searchInputRef.current?.blur();
    }
  };

  // ---- render ----

  return (
    <div className="admin2-script-logs-tab">
      {/* Script selector row */}
      <div className="admin2-log-controls">
        <select
          className="admin2-select admin2-script-select"
          value={selectedScript}
          onChange={handleScriptChange}
        >
          <option value="">Select a script...</option>
          {scriptLogs.map((log) => (
            <option key={log.script} value={log.script}>
              {log.script} ({(log.size / 1024).toFixed(1)} KB, {log.lines} lines)
            </option>
          ))}
        </select>

        <div className="admin2-log-actions">
          <button
            className="admin2-btn-secondary"
            onClick={downloadScriptLog}
            disabled={!selectedScript}
          >
            Download
          </button>
          <button
            className="admin2-btn-secondary"
            onClick={refreshScriptLog}
            disabled={!selectedScript || isRefreshing}
          >
            {isRefreshing ? <span className="admin2-spinner-sm" /> : null}
            Refresh
          </button>
          <label className="admin2-auto-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
            {autoRefresh && <span className="admin2-pulse-dot" title="Auto-refresh active" />}
          </label>
        </div>
      </div>

      {/* Search bar */}
      <div className="admin2-log-search">
        <input
          ref={searchInputRef}
          type="text"
          className="admin2-search-input"
          placeholder="Search in log... (press / to focus, Enter to search)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
      </div>

      {/* View mode toggle */}
      {selectedScript && (
        <div className="admin2-view-toggle">
          <button
            className={`admin2-view-btn ${viewMode === 'human' ? 'admin2-view-btn-active' : ''}`}
            onClick={() => setViewMode('human')}
          >
            Summary
          </button>
          <button
            className={`admin2-view-btn ${viewMode === 'technical' ? 'admin2-view-btn-active' : ''}`}
            onClick={() => setViewMode('technical')}
          >
            Technical
          </button>
        </div>
      )}

      {/* Severity filter chips (technical view only) */}
      {viewMode === 'technical' && logContent.length > 0 && (
        <div className="admin2-quick-filters">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.id}
              className={`admin2-filter-chip ${quickFilter === chip.id ? 'admin2-filter-chip-active' : ''}`}
              onClick={() => setQuickFilter(chip.id)}
            >
              {chip.icon} {chip.label}
              <span className="admin2-filter-count">{logCounts[chip.id] ?? 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Human-readable view */}
      {viewMode === 'human' && selectedScript && (
        <div className="admin2-human-log">
          {humanEvents.length === 0 ? (
            <div className="admin2-log-empty">
              <div className="admin2-log-empty-icon">📋</div>
              <p>No events parsed from this log.</p>
              <button className="admin2-btn-secondary" onClick={() => setViewMode('technical')} style={{ marginTop: '12px' }}>
                Switch to Technical View
              </button>
            </div>
          ) : (
            humanEvents.map((event, i) => (
              <div key={i} className={`admin2-human-event admin2-human-${event.type}`}>
                <span className="admin2-human-dot" />
                <div className="admin2-human-content">
                  <span className="admin2-human-text">{event.text}</span>
                  {event.timestamp && (
                    <span className="admin2-human-time">
                      {new Date(event.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Technical log content */}
      <div className="admin2-log-lines-container" style={viewMode !== 'technical' ? { display: 'none' } : {}}>
        {loading && !logContent.length ? (
          <div className="admin2-log-empty">
            <div>Loading logs...</div>
          </div>
        ) : !selectedScript ? (
          <div className="admin2-log-empty">
            <div className="admin2-log-empty-icon">📋</div>
            <p>Select a script to view logs.</p>
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="admin2-log-empty">
            <div className="admin2-log-empty-icon">🔍</div>
            <p>No logs match the current filter.</p>
            {quickFilter !== 'all' && (
              <button
                className="admin2-btn-secondary"
                onClick={() => setQuickFilter('all')}
                style={{ marginTop: '12px' }}
              >
                Show All Logs
              </button>
            )}
          </div>
        ) : (
          filteredLines.map((line) => (
            <div
              key={line.number}
              className={`admin2-log-line admin2-log-${line.severity}`}
              style={SEVERITY_STYLES[line.severity] || {}}
              onClick={() => handleCopyLine(line.content)}
              title="Click to copy"
            >
              <span className="admin2-log-line-number">{line.number}</span>
              <span className="admin2-log-line-content">
                {highlightSearchTerm(line.content, searchTerm)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {selectedScript && totalLines > 0 && (
        <div className="admin2-log-pagination">
          <button
            className="admin2-btn-secondary"
            onClick={loadPreviousPage}
            disabled={logOffset === 0}
          >
            Previous
          </button>
          <span className="admin2-pagination-info">
            Lines {logOffset + 1} – {Math.min(logOffset + logLimit, totalLines)} of {totalLines}
          </span>
          <button
            className="admin2-btn-secondary"
            onClick={loadNextPage}
            disabled={!hasMore}
          >
            Next
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="admin2-toast-container">
          <div className={`admin2-toast admin2-toast-${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
};

export default ScriptLogsTab;
